import { isBefore } from "date-fns";
import { ko } from "date-fns/locale";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled from "styled-components";
import { ReactComponent as IconCalendar } from "../Images/icon_calendar.svg";
import AlarmSimpleOk from "../Modals/AlarmSimpleOk";
import { requestDepartmentBattleList } from "../Pages/DepartmentBattle.api";

const OVERLAP_MESSAGE =
  "이미 등록된 대회와 겹치는 날짜입니다. 다른 날짜를 선택해주세요.";

const parseDateValue = (value) => {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (!value) {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
};

const normalizeDate = (value) => parseDateValue(value);

const DepartmentBattleDateOption = ({
  startTitle,
  endTitle,
  width,
  fontSize,
  gap,
  height,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  navigate,
  excludeSeasonDepartmentId,
  readOnly = false,
}) => {
  const [tempStartDate, setTempStartDate] = useState(() =>
    parseDateValue(startDate)
  );
  const [tempEndDate, setTempEndDate] = useState(() => parseDateValue(endDate));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarOpen2, setCalendarOpen2] = useState(false);
  const [showAlarm, setShowAlarm] = useState(false);
  const [excludedIntervals, setExcludedIntervals] = useState([]);

  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);

  useEffect(() => {
    setTempStartDate(parseDateValue(startDate));
    setTempEndDate(parseDateValue(endDate));
  }, [startDate, endDate]);

  const getDepartmentBattleList = async () => {
    if (readOnly) {
      return;
    }

    try {
      const response = await requestDepartmentBattleList({
        page: 1,
        offset: 1000,
        searchTitle: "",
        navigate,
      });

      if (response.code !== 200) {
        return;
      }

      const nextIntervals = response.data.rows
        .filter(
          (entry) =>
            entry.startDate &&
            entry.endDate &&
            String(entry.seasonDepartmentId) !== String(excludeSeasonDepartmentId)
        )
        .map((entry) => ({
          start: parseDateValue(entry.startDate),
          end: parseDateValue(entry.endDate),
        }));

      setExcludedIntervals(nextIntervals);
    } catch (error) {
      console.log("부서별 대항전 리스트 가져오기 중 에러:", error);
    }
  };

  const isDateExcluded = (date) =>
    excludedIntervals.some((range) => {
      const target = normalizeDate(date);
      return target >= range.start && target <= range.end;
    });

  const isRangeOverlapping = (rangeStart, rangeEnd) =>
    excludedIntervals.some((range) => {
      const normalizedStart = normalizeDate(rangeStart);
      const normalizedEnd = normalizeDate(rangeEnd);
      return normalizedStart <= range.end && normalizedEnd >= range.start;
    });

  const closeCalendars = () => {
    setCalendarOpen(false);
    setCalendarOpen2(false);
    startDatePickerRef.current?.setOpen(false);
    endDatePickerRef.current?.setOpen(false);
  };

  const applyStartDateChange = () => {
    if (readOnly) {
      return;
    }

    const nextStartDate = normalizeDate(tempStartDate);
    const currentEndDate = normalizeDate(endDate);
    const nextEndDate = isBefore(currentEndDate, nextStartDate)
      ? nextStartDate
      : currentEndDate;

    if (isRangeOverlapping(nextStartDate, nextEndDate)) {
      closeCalendars();
      setShowAlarm(true);
      return;
    }

    setStartDate(nextStartDate);
    if (isBefore(currentEndDate, nextStartDate)) {
      setEndDate(nextStartDate);
    }
    startDatePickerRef.current?.setOpen(false);
  };

  const applyEndDateChange = () => {
    if (readOnly) {
      return;
    }

    const currentStartDate = normalizeDate(startDate);
    const nextEndDate = normalizeDate(tempEndDate);

    if (isRangeOverlapping(currentStartDate, nextEndDate)) {
      closeCalendars();
      setShowAlarm(true);
      return;
    }

    setEndDate(nextEndDate);
    if (isBefore(nextEndDate, currentStartDate)) {
      setStartDate(nextEndDate);
    }
    endDatePickerRef.current?.setOpen(false);
  };

  const handleStartDateChange = (date) => {
    if (readOnly) {
      return;
    }

    const nextDate = normalizeDate(date);

    if (isBefore(nextDate, normalizeDate(new Date())) || isDateExcluded(nextDate)) {
      return;
    }

    setTempStartDate(nextDate);
  };

  const handleEndDateChange = (date) => {
    if (readOnly) {
      return;
    }

    const nextDate = normalizeDate(date);
    const currentStartDate = normalizeDate(startDate);

    if (isBefore(nextDate, currentStartDate)) {
      return;
    }

    setTempEndDate(nextDate);
  };

  return (
    <div>
      <DateFieldGrid gap={gap}>
        <FieldBlock>
          {startTitle && (
            <p style={{ fontFamily: "notoSans-medium" }}>{startTitle}</p>
          )}
          <DatePickerWrapper>
            <DatePicker
              ref={startDatePickerRef}
              excludeDateIntervals={excludedIntervals}
              customInput={
                <CustomInput
                  $isClicked={calendarOpen}
                  width={width || "100%"}
                  fontSize={fontSize}
                  height={height}
                  onChange={handleStartDateChange}
                  readOnly={readOnly}
                />
              }
              selected={tempStartDate}
              onChange={handleStartDateChange}
              selectsStart
              startDate={tempStartDate}
              endDate={tempEndDate}
              dateFormat="yyyy-MM-dd"
              dayClassName={(date) =>
                isBefore(normalizeDate(date), normalizeDate(new Date())) ||
                isDateExcluded(date)
                  ? "disabled-day"
                  : "enabled-day"
              }
              popperContainer={CalendarContainer}
              locale={ko}
              onCalendarOpen={() => {
                setCalendarOpen(true);
                getDepartmentBattleList();
              }}
              onCalendarClose={() => setCalendarOpen(false)}
              shouldCloseOnSelect={false}
              disabled={readOnly}
            >
              <div className="datepicker-footer">
                <div
                  className="datepicker-applyBtn"
                  onClick={applyStartDateChange}
                >
                  적용
                </div>
              </div>
            </DatePicker>
          </DatePickerWrapper>
        </FieldBlock>

        <FieldBlock>
          {endTitle && (
            <p style={{ fontFamily: "notoSans-medium" }}>{endTitle}</p>
          )}
          <DatePickerWrapper>
            <DatePicker
              ref={endDatePickerRef}
              excludeDateIntervals={excludedIntervals}
              customInput={
                <CustomInput
                  $isClicked={calendarOpen2}
                  width={width || "100%"}
                  fontSize={fontSize}
                  height={height}
                  onChange={handleEndDateChange}
                  readOnly={readOnly}
                />
              }
              selected={tempEndDate}
              onChange={handleEndDateChange}
              selectsEnd
              startDate={tempStartDate}
              endDate={tempEndDate}
              dateFormat="yyyy-MM-dd"
              dayClassName={(date) =>
                isBefore(
                  normalizeDate(date),
                  new Date(
                    normalizeDate(startDate).getFullYear(),
                    normalizeDate(startDate).getMonth(),
                    normalizeDate(startDate).getDate()
                  )
                ) || isDateExcluded(date)
                  ? "disabled-day"
                  : "enabled-day"
              }
              popperContainer={CalendarContainer}
              locale={ko}
              onCalendarOpen={() => {
                setCalendarOpen2(true);
                getDepartmentBattleList();
              }}
              onCalendarClose={() => setCalendarOpen2(false)}
              shouldCloseOnSelect={false}
              disabled={readOnly}
            >
              <div className="datepicker-footer">
                <div
                  className="datepicker-applyBtn"
                  onClick={applyEndDateChange}
                >
                  적용
                </div>
              </div>
            </DatePicker>
          </DatePickerWrapper>
        </FieldBlock>
      </DateFieldGrid>

      {showAlarm && (
        <AlarmSimpleOk
          title="진행 중인 대회가 있습니다"
          description={OVERLAP_MESSAGE}
          handleClickX={() => setShowAlarm(false)}
        />
      )}
    </div>
  );
};

const DateFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: ${(props) => props.gap || "16px"};
  width: 100%;
`;

const FieldBlock = styled.div`
  min-width: 0;
`;

const CustomInput = forwardRef(
  (
    { value, onClick, isClicked, width, fontSize, height, onChange, readOnly },
    ref
  ) => {
    const displayValue =
      value instanceof Date
        ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(value.getDate()).padStart(2, "0")}`
        : value || "";

    return (
      <InputWrapper
        onClick={onClick}
        ref={ref}
        $isClicked={isClicked}
        width={width}
        fontSize={fontSize}
        height={height}
        $readOnly={readOnly}
      >
        <input
          type="text"
          value={displayValue}
          onChange={onChange}
          onFocus={onClick}
          placeholder="0000-00-00"
          style={{
            border: "none",
            outline: "none",
            fontSize: fontSize || "18px",
            width: "100%",
            backgroundColor: "transparent",
            cursor: readOnly ? "default" : "pointer",
          }}
          className="placeholdercolor"
          readOnly
        />
        <div>
          <IconCalendar style={{ verticalAlign: "bottom" }} />
        </div>
      </InputWrapper>
    );
  }
);

const InputWrapper = styled.div`
  width: ${(props) => (props.width ? props.width : "290px")};
  height: ${(props) => (props.height ? props.height : "58px")};
  box-sizing: border-box;
  border-radius: 10px;
  background-color: #ffffff;
  padding: 0 20px;
  box-shadow: inset 0 0 0 1px
    ${(props) => (props.$isClicked ? "#1167ed" : "#e4e4e4")};
  margin-top: 8px;
  cursor: ${(props) => (props.$readOnly ? "default" : "pointer")};
  display: flex;
  align-items: center;
  > div {
    width: 24px;
    height: 24px;
    margin-top: 2px;
    margin-left: auto;
  }
  > p {
    font-size: ${(props) => (props.fontSize ? props.fontSize : "18px")};
  }
`;

const DatePickerWrapper = styled.div`
  width: 100%;
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    width: 100%;
  }
  .react-datepicker {
    width: 330px;
    height: auto;
    border-radius: 12px;
    border: none;
    box-shadow: inset 0 0 0 1px #e4e4e4, 0 4px 20px rgba(0, 0, 0, 0.05);
  }
  .react-datepicker-popper .react-datepicker__triangle {
    display: none;
  }
  .react-datepicker__current-month {
    font-family: "notoSans-bold";
    font-size: 16px;
    color: #09090b;
    margin-top: 6px;
  }
  .react-datepicker__day-names {
    width: 298px;
    height: 40px;
    margin: 0;
    margin-top: 25px;
  }
  .react-datepicker__day-name {
    font-family: "notoSans-bold";
    font-size: 13px;
    color: #757575;
    margin: 7px;
  }
  .react-datepicker__header {
    width: 298px;
    height: 37px;
    padding: 12px 16px;
    background-color: #ffffff;
    box-shadow: inset 0 0 0 1px #e4e4e4;
    border: none;
    border-radius: 10px 10px 0 0;
  }

  .react-datepicker__navigation {
    width: 40px;
    height: 38px;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px #e4e4e4;
    margin: 9px;
  }
  .react-datepicker__navigation-icon {
    margin-top: 8px;
    &::before {
      border-color: #6d6d6d;
      border-width: 1.5px 1.5px 0 0;
    }
    &:focus {
      outline: none;
    }
  }
  .react-datepicker__navigation-icon--previous {
    margin-right: 2px;
  }
  .react-datepicker__navigation-icon--next {
    margin-left: 2px;
  }
  .react-datepicker__month-container {
    height: auto;
  }
  .react-datepicker__month {
    margin: 0;
    margin-top: 60px;
  }
  .react-datepicker__day {
    font-family: "notoSans-medium";
    font-size: 14px;
    margin: 7px;
  }
  .enabled-day {
    color: #3f3f46;
    border-radius: 50%;
    &:hover {
      background-color: #1167ed !important;
      border-radius: 50% !important;
      color: #ffffff;
    }
  }
  .disabled-day {
    color: #b0b0b0 !important;
    background-color: transparent;
    pointer-events: none;
  }
  .react-datepicker__day--selected {
    background-color: #1167ed;
    color: #ffffff;
    border-radius: 50% !important;
    outline: none;
  }

  .react-datepicker__day--in-selecting-range {
    background-color: #1167ed;
    background-color: transparent !important;
    color: #ffffff;
    border-radius: 50%;
    outline: none;
    &:hover {
      background-color: #1167ed !important;
      color: #ffffff;
      border-radius: 50%;
    }
  }

  .react-datepicker__day--in-range {
    background-color: #1167ed !important;
    color: #ffffff !important;
    border-radius: 50%;
    outline: none;
  }

  .react-datepicker__day--outside-month {
    color: #b0b0b0;
    &:hover {
      color: #ffffff;
      border-radius: 50%;
    }
  }

  .react-datepicker__day--today {
    &:hover {
      border-radius: 50%;
    }
  }
  .react-datepicker__day--in-selecting-range {
    background-color: transparent;
    color: #3f3f46;
  }
  .react-datepicker__day--selecting-range-end {
    background-color: #1167ed;
    color: #3f3f46;
    border-radius: 50%;
  }
  .react-datepicker__day--selecting-range-start {
    background-color: #1167ed;
    color: #3f3f46;
    border-radius: 50%;
  }

  .react-datepicker__day--keyboard-selected {
    background-color: transparent;
  }

  .datepicker-footer {
    width: 298px;
    height: 44px;
    padding: 16px;
    border-top: 1px solid #f4f4f5;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: -10px;
  }
  .datepicker-applyBtn {
    width: 298px;
    height: 44px;
    border-radius: 6px;
    background-color: #1167ed;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "notoSans-bold";
    font-size: 14px;
    color: #ffffff;
    cursor: pointer;
  }
`;

export default DepartmentBattleDateOption;
